import React, { useEffect, useRef, useState } from 'react'
import {
  GoogleMap,
  useGoogleMap,
  Marker,
  useJsApiLoader
} from '@react-google-maps/api'
import { useSelector } from 'react-redux'
import { GoogleMapApiScriptOptions } from '../../api'
import { PinPointFallback } from './pin-point-fallback'

const mapOptions = {
  fullscreenControl: false,
  disableDefaultUI: true,
  panControl: true,
  zoomControl: true,
}

const mapStyles = {
  height: '300px',
}

// Determine the bound of indonesia
// This value picked manually, you can freely adjust this value
const indonesiaBound = {
  northEast: {
    lat: 4.463729,
    lng: 140.990397
  },
  southWest: {
    lat: -9.756614,
    lng: 95.582856
  },
}

/**
 * Set Initial Map Bound
 */
const SetMapBound = () => {
  const maps = useGoogleMap()

  // Set initial map bound
  useEffect(() => {
    if (maps) {
      // eslint-disable-next-line no-undef
      const bounds = new google.maps.LatLngBounds()
      bounds.extend(indonesiaBound.northEast)
      bounds.extend(indonesiaBound.southWest)
      maps.fitBounds(bounds)
    }
  }, [maps])

  return null
}

/**
 * Map Search Box
 */
const MapSearchBox = ({ value = null, onPlaceChanged }) => {
  const translation = useSelector(state => state.translation.data)
  const language = useSelector(state => state.system.language)
  const maps = useGoogleMap()
  const inputEl = useRef(null)

  const onKeypress = (e) => {
    // On enter pressed
    if (e.key === 'Enter') {
      e.preventDefault()
      return false
    }
  }

  // Set listener
  useEffect(() => {
    if (maps && inputEl.current) {
      // eslint-disable-next-line no-undef
      const autocomplete = new google.maps.places.Autocomplete(inputEl.current)
      autocomplete.bindTo('bounds', maps)
      autocomplete.setFields(['address_components', 'icon', 'name', 'geometry'])

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        // If the place has a geometry, then present it on a map.
        if (place && place.geometry && place.geometry.viewport) {
          maps.fitBounds(place.geometry.viewport);
        }

        onPlaceChanged(place)
      })
    }
  }, [maps, value, inputEl, onPlaceChanged])

  // Set value
  useEffect(() => {
    if (maps && inputEl.current && value) {
      maps.setCenter(value);
      maps.setZoom(17);  // Why 17? Because it looks good.
    }
  }, [value, maps, inputEl])

  return (
    <div className="absolute m-5 w-full pr-10 lg:pr-0 lg:w-1/2">
      <input
        ref={inputEl}
        type="text"
        className="rounded border py-2 px-4 text-sm w-full"
        placeholder={translation.GENERAL.address_find[language.name]}
        style={{borderColor: '#ddd'}}
        onKeyPress={onKeypress}
      />
    </div>
  )
}

/**
 * The Map Component
 */
const PinPointInput = ({ value, onChange }) => {
  const [marker, setMarker] = useState(false)
  const { isLoaded, loadError } = useJsApiLoader(GoogleMapApiScriptOptions)
  const [isUsingMap, setIsUsingMap] = useState(true)
  const [disableOption, setDisableOption] = useState(false)

  const onPlaceChanged = (place) => {
    if(place.geometry){
      onChange(place.geometry.location.toJSON())
    } else if (place.fallback) {
      onChange(place.data)
    }
  }

  useEffect(() => {
    if (value) {
      setMarker(value)
    }
  }, [value])

  const handleManualSubmit = (e) => {
    onPlaceChanged(e)
    setDisableOption(true)
  }

  const renderMap = () => {
    return <>
      {
        !disableOption &&
        <label className="px-5 lg:px-0 inline-block mb-4 text-sm text-gray-text">
          <input
            type="checkbox"
            className="mr-2"
            checked={isUsingMap}
            onChange={(e) => setIsUsingMap(e.target.checked)}
            disabled={disableOption}
          />
          Use Map
        </label>
      }

      {
        isUsingMap &&
        <GoogleMap
          options={mapOptions}
          mapContainerStyle={mapStyles}
        >
          <SetMapBound />
          <MapSearchBox value={value} onPlaceChanged={onPlaceChanged} />
          { marker && <Marker position={marker} /> }
        </GoogleMap>
      }
      {
        !isUsingMap &&
        <PinPointFallback
          value={value}
          onSubmit={handleManualSubmit}
        />
      }
    </>
  }

  if (loadError) {
    return <div>Map cannot be loaded right now</div>
  }

  return isLoaded ? renderMap() : <div className="bg-gray-200">Loading...</div>
}

PinPointInput.defaultProps = {
  onChange: () => {},
}

export { PinPointInput }
